/**
 * The Milky Way's well of slowed time, in kiloparsecs. Pure functions, no three.js, so the numbers can be tested.
 *
 * phi is a model potential in arbitrary units: a dark-halo term (scaled by hk), a visible-disc term and a point-like
 * central term. hk = 1 is the halo that makes the Sun orbit at the 230 km/s Gaia measured. The visible matter is
 * fixed; the slider only adds invisible mass, which is why "five times the visible" is hk * 5.
 */

/** the Sun's galactocentric radius */
export const SUN_RADIUS_KPC = 8.2;
/** what Gaia measured, km/s */
export const SUN_SPEED_KMS = 230;
/** the lattice is lifted so that this radius sits at the rim of the bowl */
export const WELL_RIM_KPC = 31.2;
/** deepest the drawn sheet may go (relative to the rim), before the lift */
export const WELL_FLOOR = -30;
/** the sheet hangs this far below the galactic plane at the rim */
export const WELL_LIFT = -1.5;
/** the halo slider's range */
export const HALO_MIN = 0.15;
export const HALO_MAX = 1.6;
/** the success window, km/s */
export const SPEED_TOLERANCE_KMS = 8;

/** Potential at radius r (kpc) for halo strength hk. Finite and smooth for r >= 0. */
export function phi(r: number, hk: number): number {
  const x = Math.max(r, 1e-3) / 16;
  return (-14 * hk * Math.log(1 + x)) / x - (5 * 1.72) / (r + 1) - 0.5 / (r + 0.04);
}

/** Circular speed, sqrt(r dPhi/dr), by central difference with h = 0.01 kpc. Arbitrary units; compare against V_REF. */
export function vCirc(r: number, hk: number): number {
  const h = 0.01;
  const lo = Math.max(r - h, 0);
  const hi = r + h;
  const g = (phi(hi, hk) - phi(lo, hk)) / (hi - lo);
  return Math.sqrt(Math.max(0, r * g));
}

/** vCirc at the Sun with hk = 1: the unit that maps to 230 km/s. */
export const V_REF = vCirc(SUN_RADIUS_KPC, 1);

/** The Sun's orbital speed in km/s for halo strength hk. */
export function sunSpeedKms(hk: number): number {
  return (SUN_SPEED_KMS * vCirc(SUN_RADIUS_KPC, hk)) / V_REF;
}

/**
 * The halo strength at which the Sun orbits at targetKms, by bisection on [lo, hi] (40 halvings). The speed rises
 * with hk, so there is one answer; a target outside the range comes back as the nearer end.
 */
export function haloForSpeed(targetKms: number, lo: number, hi: number): number {
  let a = lo;
  let b = hi;
  for (let i = 0; i < 40; i++) {
    const mid = (a + b) / 2;
    if (sunSpeedKms(mid) < targetKms) a = mid; else b = mid;
  }
  return (a + b) / 2;
}

/** The Sun marker's angular speed on its ring, rad/s: 0.22 at the real halo, slower with less mass. */
export function sunAngularRate(hk: number): number {
  return (0.22 * vCirc(SUN_RADIUS_KPC, hk)) / V_REF;
}

/** Invisible mass as a multiple of the visible mass. */
export function invisibleMassRatio(hk: number): number {
  return hk * 5;
}

/** True when a displayed speed is close enough to what Gaia measured. */
export function withinMeasured(speedKms: number, tol: number = SPEED_TOLERANCE_KMS): boolean {
  return Math.abs(speedKms - SUN_SPEED_KMS) <= tol;
}

/** Height of the drawn sheet at radius r, given the potential at the rim. Used by the lattice, which computes the rim once. */
export function wellZFromRim(r: number, hk: number, phiRim: number): number {
  return Math.max(phi(r, hk) - phiRim, WELL_FLOOR) + WELL_LIFT;
}

/** Height of the drawn sheet at radius r: the potential relative to the rim, floored, then lifted. */
export function wellZ(r: number, hk: number): number {
  return wellZFromRim(r, hk, phi(WELL_RIM_KPC, hk));
}
